import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAdminRefunds, createAdminRefund } from "../../../redux/actions/profile";

const BG = "#2c2d3f";

const STATUS_COLOR = { processed: "#2ec4b6", pending: "#f4a261", failed: "#e63946" };

export default function Refunds() {
  const dispatch = useDispatch();
  const refunds = useSelector((s) => s.profile.adminRefunds);
  const meta = useSelector((s) => s.profile.adminRefundsMeta);

  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ transaction: "", amount: "" });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { dispatch(getAdminRefunds({ page: 1, perPage: 50 })); }, [dispatch]);

  const load = (p) => { setPage(p); dispatch(getAdminRefunds({ page: p, perPage: 50 })); };

  const submit = async () => {
    if (!form.transaction.trim()) return setMsg({ type: "error", text: "Transaction reference is required" });
    setSubmitting(true);
    setMsg(null);
    const body = { transaction: form.transaction.trim() };
    if (form.amount) body.amount = Math.round(parseFloat(form.amount) * 100);
    const res = await dispatch(createAdminRefund(body));
    setSubmitting(false);
    if (res?.success) {
      setMsg({ type: "success", text: res.message || "Refund initiated successfully" });
      setForm({ transaction: "", amount: "" });
      dispatch(getAdminRefunds({ page: 1, perPage: 50 }));
    } else {
      setMsg({ type: "error", text: res?.message || "Failed to initiate refund" });
    }
  };

  const fmtMoney = (k) => k != null ? `₦${(k / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}` : "—";
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div style={S.root}>
      <div style={S.pageHeader}>
        <div>
          <h2 style={S.heading}>Refunds</h2>
          <p style={S.headingSub}>{meta?.total || refunds.length} refund records</p>
        </div>
        <button style={S.btn} onClick={() => { setShowModal(true); setMsg(null); }}>
          <i className="bi bi-plus-lg" style={{ marginRight: 6 }} />New Refund
        </button>
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {["ID", "Transaction Reference", "Amount", "Status", "Refunded At"].map((h) => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {refunds.length === 0 ? (
              <tr><td colSpan={5} style={{ ...S.td, textAlign: "center", color: "#bbb", padding: 32 }}>No refunds found</td></tr>
            ) : refunds.map((r, i) => (
              <tr key={r.id || i} style={S.tr}>
                <td style={S.td}><span style={S.mono}>{r.id}</span></td>
                <td style={S.td}><span style={S.mono}>{r.transaction?.reference || r.transaction || "—"}</span></td>
                <td style={{ ...S.td, fontWeight: 700, color: BG }}>{fmtMoney(r.amount)}</td>
                <td style={S.td}>
                  <span style={{ background: (STATUS_COLOR[r.status] || "#888") + "18", color: STATUS_COLOR[r.status] || "#888", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                    {r.status || "—"}
                  </span>
                </td>
                <td style={{ ...S.td, color: "#888" }}>{fmtDate(r.refunded_at || r.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={S.pagination}>
        <button style={S.pgBtn} disabled={page <= 1} onClick={() => load(page - 1)}>‹ Previous</button>
        <span style={{ color: "#888", fontSize: 13 }}>Page {page}</span>
        <button style={S.pgBtn} onClick={() => load(page + 1)}>Next ›</button>
      </div>

      {showModal && (
        <div style={S.overlay} onClick={() => setShowModal(false)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <div style={S.modalTitle}>Create Refund</div>
                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>Initiate a full or partial refund on Paystack</div>
              </div>
              <button style={S.closeBtn} onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div style={S.modalBody}>
              <label style={S.label}>Transaction Reference *</label>
              <input
                style={S.inputFull}
                placeholder="e.g. T_abc123xyz"
                value={form.transaction}
                onChange={(e) => setForm({ ...form, transaction: e.target.value })}
              />
              <label style={S.label}>Amount (₦) — leave blank for full refund</label>
              <input
                style={S.inputFull}
                placeholder="e.g. 500"
                type="number"
                min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
              {msg && (
                <div style={{ borderRadius: 8, padding: "10px 14px", fontSize: 13, marginTop: 12, background: msg.type === "success" ? "rgba(46,196,182,0.1)" : "rgba(230,57,70,0.08)", color: msg.type === "success" ? "#2ec4b6" : "#e63946", border: `1px solid ${msg.type === "success" ? "#2ec4b6" : "#e63946"}` }}>
                  {msg.text}
                </div>
              )}
              <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                <button style={S.btn} onClick={submit} disabled={submitting}>
                  {submitting ? "Processing…" : "Initiate Refund"}
                </button>
                <button style={S.btnSecondary} onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  root: { paddingBottom: 40 },
  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 24 },
  heading: { fontSize: 22, fontWeight: 800, color: BG, margin: 0 },
  headingSub: { fontSize: 13, color: "#888", marginTop: 4 },
  btn: { background: BG, border: "none", borderRadius: 8, color: "#fff", fontWeight: 600, padding: "8px 20px", cursor: "pointer", fontSize: 13, display: "inline-flex", alignItems: "center" },
  btnSecondary: { background: "#f5f5f5", border: "1.5px solid #e0e0e0", borderRadius: 8, color: "#666", fontWeight: 500, padding: "8px 16px", cursor: "pointer", fontSize: 13 },
  tableWrap: { background: "#fff", borderRadius: 10, overflowX: "auto", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", WebkitOverflowScrolling: "touch" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { background: BG, color: "#fff", padding: "11px 14px", textAlign: "left", fontSize: 12, fontWeight: 600 },
  tr: { borderBottom: "1px solid #f5f5f5" },
  td: { padding: "11px 14px", fontSize: 13, color: "#555", verticalAlign: "middle" },
  mono: { fontFamily: "monospace", fontSize: 12, color: "#888" },
  pagination: { display: "flex", alignItems: "center", gap: 16, marginTop: 16, justifyContent: "flex-end" },
  pgBtn: { background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: 8, color: "#555", padding: "7px 18px", cursor: "pointer", fontSize: 13, fontWeight: 500 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" },
  modal: { background: "#fff", borderRadius: 12, width: 440, maxWidth: "95vw", boxShadow: "0 8px 32px rgba(0,0,0,0.15)" },
  modalHeader: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "20px 24px", borderBottom: "1px solid #f0f0f0" },
  modalTitle: { fontSize: 16, fontWeight: 700, color: BG },
  closeBtn: { background: "none", border: "none", color: "#aaa", fontSize: 18, cursor: "pointer", lineHeight: 1 },
  modalBody: { padding: "20px 24px 24px" },
  label: { display: "block", color: "#555", fontSize: 13, fontWeight: 600, marginBottom: 6, marginTop: 14 },
  inputFull: { width: "100%", border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "9px 12px", fontSize: 13, color: "#333", background: "#fafafa", boxSizing: "border-box", outline: "none" },
};
